import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';
import { RtModal, RtModalField, RtModalSection } from '../ui/RtModal';

const ProjectConfigModal = ({ isOpen, onClose, onSave, editingConfig, isEditMode }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    max_scenarios: '',
    max_test_cases: ''
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Fetch projects on modal open
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      if (isEditMode && editingConfig) {
        setFormData({
          project_id: editingConfig.project_id || '',
          max_scenarios: editingConfig.max_scenarios || '',
          max_test_cases: editingConfig.max_test_cases || ''
        });
      } else {
        setFormData({
          project_id: '',
          max_scenarios: '',
          max_test_cases: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, isEditMode, editingConfig]);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await axiosInstance.get('/projects/list_project?page=1&page_size=100&pro_status=active');

      // Map the response to match the expected format
      const apiProjects = response.projects ? response.projects.map(project => ({
        id: project.project_id,
        name: project.project_name,
        status: 'active'
      })) : [];

      setProjects(apiProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_id) {
      newErrors.project_id = 'Please select a project';
    }

    if (!formData.max_scenarios || formData.max_scenarios < 1) {
      newErrors.max_scenarios = 'Max scenarios must be at least 1';
    }

    if (!formData.max_test_cases || formData.max_test_cases < 1) {
      newErrors.max_test_cases = 'Max test cases must be at least 1';
    }

    if (formData.max_scenarios && formData.max_scenarios > 999) {
      newErrors.max_scenarios = 'Max scenarios cannot exceed 999';
    }

    if (formData.max_test_cases && formData.max_test_cases > 999) {
      newErrors.max_test_cases = 'Max test cases cannot exceed 999';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    // For numeric fields, ensure only positive integers
    if (field === 'max_scenarios' || field === 'max_test_cases') {
      if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
          setErrors(prev => ({ ...prev, [field]: '' }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const submitData = {
      project_id: parseInt(formData.project_id),
      max_scenarios: parseInt(formData.max_scenarios),
      max_test_cases: parseInt(formData.max_test_cases)
    };

    try {
      await onSave(submitData);
    } catch (error) {
      console.error('Error saving configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setFormData({
      project_id: '',
      max_scenarios: '',
      max_test_cases: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <RtModal
      open={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit Project Configuration' : 'Add Project Configuration'}
      subtitle={
        isEditMode ? 'Update project limits and settings' : 'Set limits for project resources'
      }
      size="md"
      closeDisabled={loading}
      closeOnBackdrop={!loading}
      footer={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="rt-project-config-form"
            className="rt-btn rt-btn--primary"
            disabled={loading || projectsLoading}
          >
            {loading
              ? isEditMode
                ? 'Updating…'
                : 'Saving…'
              : isEditMode
                ? 'Update Configuration'
                : 'Save Configuration'}
          </button>
        </>
      }
    >
      <form id="rt-project-config-form" onSubmit={handleSubmit}>
        <RtModalSection title="Project limits">
          <RtModalField
            label="Project"
            htmlFor="project_id"
            required
            error={errors.project_id || null}
          >
            {projectsLoading ? (
              <p className="rt-modal__hint">Loading projects…</p>
            ) : (
              <select
                id="project_id"
                value={formData.project_id}
                onChange={(e) => handleInputChange('project_id', e.target.value)}
                disabled={isEditMode || loading}
                className={errors.project_id ? 'error' : ''}
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
          </RtModalField>

          <RtModalField
            label="Max Scenarios"
            htmlFor="max_scenarios"
            required
            error={errors.max_scenarios || null}
            hint={!errors.max_scenarios ? 'Maximum number of scenarios allowed for this project' : null}
          >
            <input
              type="text"
              id="max_scenarios"
              value={formData.max_scenarios}
              onChange={(e) => handleInputChange('max_scenarios', e.target.value)}
              maxLength="3"
              placeholder="Enter max scenarios"
              disabled={loading}
              className={errors.max_scenarios ? 'error' : ''}
            />
          </RtModalField>

          <RtModalField
            label="Max Test Cases"
            htmlFor="max_test_cases"
            required
            error={errors.max_test_cases || null}
            hint={!errors.max_test_cases ? 'Maximum number of test cases allowed for this project' : null}
          >
            <input
              type="text"
              id="max_test_cases"
              value={formData.max_test_cases}
              onChange={(e) => handleInputChange('max_test_cases', e.target.value)}
              maxLength="3"
              placeholder="Enter max test cases"
              disabled={loading}
              className={errors.max_test_cases ? 'error' : ''}
            />
          </RtModalField>
        </RtModalSection>
      </form>
    </RtModal>
  );
};

export default ProjectConfigModal;
